import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";

export interface AnimatedTabItem {
  value: string;
  label: string;
  icon: LucideIcon;
  // Small count pill on the icon's top-right corner -- e.g. unread Comms
  // Book messages, open jobs waiting to be closed off. 0/undefined shows
  // nothing; capped at 99+ so it never outgrows the pill.
  badge?: number;
}

interface AnimatedTabBarProps {
  tabs: AnimatedTabItem[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  // Opt-in: whenever the active tab belongs to this row, slide it into slot
  // 1 -- everything else keeps its existing relative order and just shifts
  // over. Lets a row stay narrow/predictable (the current tab is always in
  // the same spot) without needing to scroll to find it. Off by default so
  // existing single-row usages (e.g. desktop) keep a fixed tab order.
  reorderActiveToFront?: boolean;
  // Which way the active icon pops up out of the wave notch. "down" mirrors
  // the whole rise -- surface on top, notch/icon rising toward the bottom
  // edge instead of the top. Lets two rows stacked back-to-back (one "up",
  // one "down") each rise away from the shared seam, so they can sit flush
  // with zero gap and never visually collide.
  wavePosition?: "up" | "down";
  // Where the RESTING (non-active) label sits relative to its icon -- the
  // active label always stays below its icon regardless, since moving it
  // would put it in the path of that icon's own rise animation. "top" is
  // for rows whose resting icons sit low in the box (close to a seam,
  // e.g. a stacked "up" row next to a "down" row below it), where a label
  // underneath would be cramped right against that seam.
  labelPosition?: "bottom" | "top";
}

/**
 * AnimatedTabBar
 *
 * Bottom-nav-style tab strip where the active icon pops up out of a
 * wave-shaped notch that slides to follow it (adapted from
 * https://codepen.io/abxlfazl/pen/VwKzaEm "Animated Tab Bar" by Mauricio
 * Bucardo -- same clip-path blob shape, recoloured to this app's gold
 * branding and given always-visible text labels). Unlike the original demo,
 * this is built to live inside a horizontally-scrollable box (mobile has
 * more tabs than fit on screen): the whole "risen" state -- wave + popped
 * icon -- is reserved as vertical space INSIDE the bar's own box rather
 * than relying on free overflow above it, since a scrollable axis clips the
 * other axis too.
 *
 * Not wrapped in shadcn's Tabs -- same controlled value/onValueChange
 * pattern as FolderTabNav, the caller owns the active-tab state and
 * conditionally renders content itself.
 */
const AnimatedTabBar: React.FC<AnimatedTabBarProps> = ({
  tabs,
  value,
  onValueChange,
  className,
  reorderActiveToFront = false,
  wavePosition = "up",
  labelPosition = "bottom",
}) => {
  const waveId = useId();
  const barRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const waveRef = useRef<HTMLDivElement | null>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Display order, independent of the tabs prop's own order -- only used
  // when reorderActiveToFront is on. Seeded from tabs and kept in sync with
  // it below (tabs can change shape, e.g. a conditional tab appearing).
  const [order, setOrder] = useState<string[]>(() => tabs.map((t) => t.value));
  const prevRectsRef = useRef<Record<string, DOMRect> | null>(null);

  useEffect(() => {
    setOrder((prev) => {
      const stillPresent = prev.filter((v) => tabs.some((t) => t.value === v));
      const added = tabs.map((t) => t.value).filter((v) => !stillPresent.includes(v));
      const next = [...stillPresent, ...added];
      if (next.length === prev.length && next.every((v, i) => v === prev[i])) return prev;
      return next;
    });
  }, [tabs]);

  // Active tab moved to this row -- capture where every button currently
  // sits (before React reorders the DOM) so the effect below can animate
  // from there once the reorder actually lands.
  useLayoutEffect(() => {
    if (!reorderActiveToFront) return;
    if (order[0] === value || !order.includes(value)) return;
    const rects: Record<string, DOMRect> = {};
    Object.entries(buttonRefs.current).forEach(([k, el]) => {
      if (el) rects[k] = el.getBoundingClientRect();
    });
    prevRectsRef.current = rects;
    setOrder((prev) => [value, ...prev.filter((v) => v !== value)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const displayTabs = reorderActiveToFront
    ? (order.map((v) => tabs.find((t) => t.value === v)).filter(Boolean) as AnimatedTabItem[])
    : tabs;

  const positionWave = () => {
    const track = trackRef.current;
    const wave = waveRef.current;
    if (!track || !wave) return;
    const activeButton = buttonRefs.current[value];
    // Splitting the bar into multiple rows (e.g. a mobile 2-row layout)
    // means any given instance's own tabs might not include the active
    // value at all -- hide this row's wave rather than leaving it stuck
    // under whichever button was last active here.
    if (!activeButton) {
      wave.style.opacity = "0";
      return;
    }
    wave.style.opacity = "1";
    // Both rects are viewport-relative at the same instant, so their
    // difference is the button's position relative to the track's own left
    // edge regardless of how far the bar is scrolled -- if the bar scrolls,
    // track and button shift left together by the same amount, so the
    // difference stays constant. No separate scroll listener needed.
    const trackRect = track.getBoundingClientRect();
    const btnRect = activeButton.getBoundingClientRect();
    const left = Math.floor(btnRect.left - trackRect.left - (wave.offsetWidth - btnRect.width) / 2);
    // Mirror the wave shape itself for a "down" row -- flips the hump to
    // point toward the bottom edge instead of the top. Only the shape
    // flips; the horizontal slide-to-follow-the-active-tab logic is
    // identical either way.
    const flip = wavePosition === "down" ? " scaleY(-1)" : "";
    wave.style.transform = `translate3d(${left}px, 0, 0)${flip}`;
  };

  useLayoutEffect(() => {
    // When a reorder is about to happen (see the effect below), skip
    // positioning the wave here -- it would target the button's pre-reorder
    // slot, then get immediately overwritten once the reorder effect runs,
    // and the two writes racing each other is what made the wave visibly
    // detach from the icon mid-transition. Let the reorder effect be the
    // single source of truth for where the wave goes in that case.
    if (reorderActiveToFront && order.includes(value) && order[0] !== value) return;
    positionWave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, tabs.length]);

  // Runs after `order` actually changes and the DOM has re-laid-out to
  // match -- slides each button from where it stood before the reorder
  // (captured above) to its new spot, so items appear to glide into place
  // rather than pop. Read positionWave() first, while buttons still sit at
  // their natural (untransformed) final position, so the wave doesn't
  // briefly target the wrong spot mid-animation.
  useLayoutEffect(() => {
    const prev = prevRectsRef.current;
    positionWave();
    if (prev) {
      Object.entries(buttonRefs.current).forEach(([k, el]) => {
        if (!el) return;
        const before = prev[k];
        if (!before) return;
        const after = el.getBoundingClientRect();
        const dx = before.left - after.left;
        if (Math.abs(dx) < 1) return;
        el.style.transition = "none";
        el.style.transform = `translateX(${dx}px)`;
        requestAnimationFrame(() => {
          el.style.transition = "transform 0.35s cubic-bezier(0.2,0.8,0.2,1)";
          el.style.transform = "translateX(0)";
        });
      });
      prevRectsRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  useEffect(() => {
    window.addEventListener("resize", positionWave);
    return () => window.removeEventListener("resize", positionWave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When multiple AnimatedTabBars are stacked as rows (e.g. the mobile
  // 2-row driver layout), the row that doesn't hold the current selection
  // dims to a lighter gold -- a quick visual cue for which row is "live"
  // without needing to read the labels.
  const hasActiveTab = tabs.some((t) => t.value === value);

  return (
    <div
      className={`animated-tab-bar-outer ${hasActiveTab ? "" : "animated-tab-bar-outer--dimmed"} ${
        wavePosition === "down" ? "animated-tab-bar-outer--wave-down" : ""
      } ${labelPosition === "top" ? "animated-tab-bar-outer--label-top" : ""} ${className ?? ""}`}
    >
      <div className="animated-tab-bar" ref={barRef}>
        <div className="animated-tab-bar__track" ref={trackRef}>
          <div className="animated-tab-bar__surface" />
          <div className="animated-tab-bar__wave" ref={waveRef} style={{ clipPath: `url(#${waveId})` }} />
          {displayTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = value === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                ref={(el) => {
                  buttonRefs.current[tab.value] = el;
                }}
                onClick={() => onValueChange(tab.value)}
                aria-pressed={isActive}
                className="animated-tab-bar__item"
              >
                <span className={`animated-tab-bar__icon-wrap ${isActive ? "active" : ""}`}>
                  <Icon
                    key={isActive ? `${tab.value}-active` : `${tab.value}-inactive`}
                    className={`animated-tab-bar__icon ${isActive ? "animate-tab-icon-pop" : ""}`}
                  />
                  {!!tab.badge && (
                    <span className="animated-tab-bar__badge">{tab.badge > 99 ? "99+" : tab.badge}</span>
                  )}
                </span>
                <span className={`animated-tab-bar__label ${isActive ? "active" : ""}`}>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Same clip-path blob path/viewBox as the original -- objectBoundingBox +
          scale makes the same path data apply proportionally to the wave div's
          own box size, whatever that ends up being. */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <clipPath id={waveId} clipPathUnits="objectBoundingBox" transform="scale(0.0049285362247413 0.021978021978022)">
          <path
            d="M6.7,45.5c5.7,0.1,14.1-0.4,23.3-4c5.7-2.3,9.9-5,18.1-10.5c10.7-7.1,11.8-9.2,20.6-14.3c5-2.9,9.2-5.2,15.2-7
            c7.1-2.1,13.3-2.3,17.6-2.1c4.2-0.2,10.5,0.1,17.6,2.1c6.1,1.8,10.2,4.1,15.2,7c8.8,5,9.9,7.1,20.6,14.3c8.3,5.5,12.4,8.2,18.1,10.5
            c9.2,3.6,17.6,4.2,23.3,4H6.7z"
          />
        </clipPath>
      </svg>
    </div>
  );
};

export default AnimatedTabBar;
